import React, { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import moment from 'moment';

import {
  IFakturowniaInvoiceListRow,
  IInvoiceWithNet,
  useAllFakturowniaInvoices,
  withNetOutstanding,
} from 'core/hooks';
import { MoneyCell } from 'molecules';
import { PATHS } from 'router/config';

import * as S from '../FakturowniaInvoices/FakturowniaInvoices.styled';

interface IInvoicesBreakdownProps {
  dateFrom: string;
  dateTo: string;
}

type TabId = 'issued' | 'paid' | 'expected' | 'overdue' | 'corrections';

const TABS: TabId[] = ['issued', 'paid', 'expected', 'overdue', 'corrections'];

const fmt = (n: number) => n.toFixed(2);

const isPaidStatus = (status?: string | null) =>
  (status || '').toUpperCase() === 'PAID';

/**
 * Five-tab issued-invoice breakdown — the dashboard's trust-the-numbers
 * panel (Phase 8). Replaces the old PendingSection + OverdueList: now the
 * financier picks any tab and sees a header chip with `N invoices · X PLN`
 * plus the concrete list — so the KPI strip becomes verifiable.
 *
 * One server fetch (issued + date range) feeds all five tabs, derived
 * client-side. Tabs 1–4 run `withNetOutstanding` first (corrections
 * dropped, parents netted); tab 5 surfaces the corrections themselves
 * with negative amounts so nothing is hidden — only re-categorised.
 *
 * Phase 9.1: source is `useAllFakturowniaInvoices` which auto-paginates
 * until every backend page is exhausted. The old `perPage: 500` cap that
 * could silently truncate year-views is gone — every invoice in the
 * filtered period feeds the tabs.
 */
export const InvoicesBreakdown = memo(
  ({ dateFrom, dateTo }: IInvoicesBreakdownProps) => {
    const { t } = useTranslation();
    const history = useHistory();
    const [tab, setTab] = useState<TabId>('expected');

    const { data, loading } = useAllFakturowniaInvoices({
      direction: 'issued',
      dateFrom,
      dateTo,
    });

    const netted: IInvoiceWithNet[] = useMemo(() => withNetOutstanding(data), [
      data,
    ]);

    const issued = netted;
    const paid = useMemo(() => netted.filter((r) => isPaidStatus(r.status)), [
      netted,
    ]);
    const expected = useMemo(() => netted.filter((r) => r.netOutstanding > 0), [
      netted,
    ]);
    const overdue = useMemo(() => {
      const todayMs = moment().startOf('day').valueOf();
      return expected.filter(
        (r) => r.paymentTo && moment(r.paymentTo).valueOf() < todayMs,
      );
    }, [expected]);
    const corrections = useMemo(() => data.filter((r) => r.isCorrection), [
      data,
    ]);

    const counts: Record<TabId, number> = {
      issued: issued.length,
      paid: paid.length,
      expected: expected.length,
      overdue: overdue.length,
      corrections: corrections.length,
    };

    const sums: Record<TabId, number> = {
      issued: issued.reduce((s, r) => s + (r.priceGross || 0), 0),
      paid: paid.reduce((s, r) => s + (r.paid || 0), 0),
      expected: expected.reduce((s, r) => s + r.netOutstanding, 0),
      overdue: overdue.reduce((s, r) => s + r.netOutstanding, 0),
      corrections: corrections.reduce((s, r) => s + (r.priceGross || 0), 0),
    };

    const counterpartyLabel = (row: IFakturowniaInvoiceListRow) =>
      row.linkedStudent?.user?.email ||
      row.buyerEmail ||
      row.buyerName ||
      row.linkedGroup?.name ||
      '—';

    const openDetail = (id: string) =>
      history.push(PATHS.financesInvoice.replace(':id', id));

    const renderNettedRows = (rows: IInvoiceWithNet[], showNet: boolean) => (
      <S.Table>
        <thead>
          <tr>
            <th>{t('pages.fakturownia.invoices.columns.number')}</th>
            <th>{t('pages.fakturownia.invoices.columns.counterparty')}</th>
            <th>{t('pages.fakturownia.invoices.columns.date')}</th>
            <th style={{ textAlign: 'right' }}>
              {showNet
                ? t('pages.fakturownia.invoices.columns.outstanding')
                : t('pages.fakturownia.invoices.columns.gross')}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} onClick={() => openDetail(row.id)}>
              <S.NameCell>{row.number}</S.NameCell>
              <td>{counterpartyLabel(row)}</td>
              <td style={{ color: '#64748b', whiteSpace: 'nowrap' }}>
                {row.paymentTo
                  ? moment(row.paymentTo).format('DD.MM.YYYY')
                  : row.issueDate
                  ? moment(row.issueDate).format('DD.MM.YYYY')
                  : '—'}
              </td>
              <td>
                <MoneyCell
                  amount={showNet ? row.netOutstanding : row.priceGross}
                  currency={row.currency}
                  highlightOutstanding={showNet}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </S.Table>
    );

    const renderCorrectionRows = (rows: IFakturowniaInvoiceListRow[]) => (
      <S.Table>
        <thead>
          <tr>
            <th>{t('pages.fakturownia.invoices.columns.number')}</th>
            <th>{t('pages.fakturownia.invoices.columns.counterparty')}</th>
            <th>{t('pages.fakturownia.invoices.columns.date')}</th>
            <th style={{ textAlign: 'right' }}>
              {t('pages.fakturownia.invoices.columns.gross')}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.id}
              onClick={() => openDetail(row.id)}
              style={{ background: '#fafafa' }}
            >
              <S.NameCell>
                {row.number}
                {row.parentInvoice ? (
                  <div
                    style={{
                      fontSize: 11,
                      color: '#64748b',
                      fontWeight: 400,
                      marginTop: 2,
                    }}
                  >
                    {t('pages.fakturownia.invoices.correctionTo', {
                      number: row.parentInvoice.number,
                    })}
                  </div>
                ) : null}
              </S.NameCell>
              <td>{counterpartyLabel(row)}</td>
              <td style={{ color: '#64748b', whiteSpace: 'nowrap' }}>
                {row.issueDate
                  ? moment(row.issueDate).format('DD.MM.YYYY')
                  : '—'}
              </td>
              <td>
                <MoneyCell
                  amount={row.priceGross}
                  currency={row.currency}
                  negative
                />
              </td>
            </tr>
          ))}
        </tbody>
      </S.Table>
    );

    const visibleRows: IFakturowniaInvoiceListRow[] | IInvoiceWithNet[] =
      tab === 'issued'
        ? issued
        : tab === 'paid'
        ? paid
        : tab === 'expected'
        ? expected
        : tab === 'overdue'
        ? overdue
        : corrections;

    const showNet = tab === 'expected' || tab === 'overdue';

    return (
      <S.Card>
        <S.CardTitle>{t('pages.financeDashboard.breakdown.title')}</S.CardTitle>

        <S.SegmentTabs style={{ marginBottom: 12, flexWrap: 'wrap' }}>
          {TABS.map((id) => (
            <S.SegmentTab
              key={id}
              type="button"
              active={tab === id}
              onClick={() => setTab(id)}
            >
              {t(`pages.financeDashboard.breakdown.tabs.${id}`)}
              <span style={{ marginLeft: 6, color: '#94a3b8' }}>
                ({counts[id]})
              </span>
            </S.SegmentTab>
          ))}
        </S.SegmentTabs>

        <div
          style={{
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: 8,
            padding: '12px 14px',
            marginBottom: 12,
            display: 'flex',
            alignItems: 'baseline',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <strong style={{ fontSize: 18, color: '#0f172a' }}>
            {t('pages.financeDashboard.breakdown.summary', {
              count: counts[tab],
              sum: fmt(sums[tab]),
            })}
          </strong>
          <span style={{ fontSize: 12, color: '#64748b' }}>
            {t(`pages.financeDashboard.breakdown.hint.${tab}`)}
          </span>
        </div>

        {loading && visibleRows.length === 0 ? (
          <S.EmptyState>
            {t('pages.financeDashboard.breakdown.loading')}
          </S.EmptyState>
        ) : visibleRows.length === 0 ? (
          <S.EmptyState>
            {t('pages.financeDashboard.breakdown.empty')}
          </S.EmptyState>
        ) : tab === 'corrections' ? (
          renderCorrectionRows(visibleRows as IFakturowniaInvoiceListRow[])
        ) : (
          renderNettedRows(visibleRows as IInvoiceWithNet[], showNet)
        )}
      </S.Card>
    );
  },
);
